import React, { useRef } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Heading, Text } from '@fiverr-private/typography';
import { GigCardCarousel, SmallListViewListings } from '@fiverr-private/gig_listings';
import { FiverrLogo } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { useBQ } from '../../hooks/useBQ';
import { useWidgetImpression } from '../../hooks/useAfp1Impression';
import { sendMixpanelWidgetClickEvent } from '../../utils/mixpanel';
import { transformGigData } from '../../utils/gigDataTransformer';
import { ExploreMoreServices } from '../ExploreMoreServices';
import { TrackingLink } from '../TrackingLink';
import { GigMockCard } from '../GigMockCard';
import { Gig, BaseWidgetProps } from '../../types';
import { brandClass, serviceButtonClass } from './styles.ve.css';

interface GigsListingNewProps extends BaseWidgetProps {
  gigs: Gig[];
  widgetTitle?: string;
  exploreSlug?: string;
  widgetMoreButton?: string;
}

export const GigsListingNew: React.FC<GigsListingNewProps> = ({
  gigs,
  widgetTitle,
  affiliateId,
  exploreSlug,
  utmCampaign,
  brand,
  afp,
  afp1,
  widgetMoreButton,
  widgetType,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { isTouch, userId, pageCtxId } = getContext();

  const clickName = 'fiverr_logo_link';
  const clickSection = 'fiverr_name';

  const { sendBQEvent } = useBQ();

  useWidgetImpression({ affiliateId, afp1: afp1 || '', widgetType }, ref);

  const onFiverrClick = () => {
    sendBQEvent({
      type: 'nga.fiverr_name_click',
      affiliateId,
      widgetType,
      afp1: afp1 || '',
    });
    sendMixpanelWidgetClickEvent({
      affiliateId,
      clickName,
      clickSection,
      hostReference: afp1 || '',
    });
  };

  const marketingParams = {
    affiliateId,
    utmCampaign: utmCampaign || '',
    brand: brand || '',
    afp: afp || '',
    afp1: afp1 || '',
    afp2: clickName,
    afp3: clickSection,
    widgetType,
  };

  const useMockGig = gigs.length === 0;

  const transformedGigs = transformGigData(gigs, marketingParams);

  const enrichedGigContext = {
    impressionEnrichment: {
      listings: {
        page_ctx_id: pageCtxId,
      },
    },
    context: {
      sourcePage: 'GigWidgets',
      sourceComponent: 'GigWidgets',
      viewportType: 'tags.show',
      controllerAction: 'tags#show',
      userId,
      pageCtxId,
      queryParameters: marketingParams,
      currency: {
        name: 'USD',
        symbol: '$',
        template: '{{amount}}',
        forceRound: true,
        forceRoundFromAmount: 0,
      },
    },
    isTouch,
  };

  const GigListingComponent = gigs.length === 1 ? SmallListViewListings : GigCardCarousel;

  return (
    <Container ref={ref} display="flex" direction="column" gap="6" maxWidth="1212px" margin="auto">
      {widgetTitle && (
        <Heading size="h_md" paddingX={{ default: '0', sm: '6' }}>
          {widgetTitle}
        </Heading>
      )}
      <Container paddingX={{ default: '0', sm: '6' }}>
        {useMockGig ? (
          <GigMockCard />
        ) : (
          <GigListingComponent
            gigs={transformedGigs}
            sliderBaseNum={gigs.length}
            isExperiential
            {...enrichedGigContext}
          />
        )}
      </Container>
      <Stack
        direction={{ default: 'column', sm: 'row' }}
        justifyContent={exploreSlug ? 'spaceBetween' : 'flexEnd'}
        alignItems={{ default: 'flexStart', sm: 'center' }}
        paddingX={{ default: '0', sm: '6' }}
      >
        {exploreSlug && (
          <ExploreMoreServices
            className={serviceButtonClass}
            affiliateId={affiliateId}
            widgetType={widgetType}
            brand={brand}
            afp={afp}
            afp1={afp1}
            utmCampaign={utmCampaign}
            exploreSlug={exploreSlug}
          >
            {widgetMoreButton}
          </ExploreMoreServices>
        )}
        <TrackingLink onClick={onFiverrClick} {...marketingParams}>
          <Text size="b_sm" fontWeight="semibold" color="bodySecondary" className={brandClass}>
            Services on <FiverrLogo />
          </Text>
        </TrackingLink>
      </Stack>
    </Container>
  );
};
